import { useEffect } from 'react';
import { DEFAULT_SYSTEM_NAME, systemNameOf } from '@stockpilot/shared';
import { useAppSelector } from '@/app/hooks';

const REMEMBERED_NAME = 'stockpilot.systemName';

/**
 * What to call the system on screen.
 *
 * A business renames it in settings; everything else — the shell, the browser
 * tab, anywhere the product names itself — reads it from here, so renaming is
 * one field rather than a search through the source.
 */
export function useSystemName(): string {
  const name = systemNameOf(
    useAppSelector((state) => state.tenant.context?.settings?.systemName),
  );

  // Remember it for the screens that run before any business is known.
  useEffect(() => {
    try {
      window.localStorage.setItem(REMEMBERED_NAME, name);
    } catch {
      // A browser that refuses storage simply forgets; the default still works.
    }
  }, [name]);

  return name;
}

/**
 * What to call the system before anyone has signed in.
 *
 * The login page runs before any business is known, so the name cannot be
 * fetched — it is whatever this browser last saw. A fresh device shows the
 * installation default, which is the honest answer: we do not know yet whose
 * login page this is.
 */
export function useSignedOutSystemName(): string {
  try {
    return window.localStorage.getItem(REMEMBERED_NAME) || DEFAULT_SYSTEM_NAME;
  } catch {
    return DEFAULT_SYSTEM_NAME;
  }
}

/** Keeps the browser tab in step with the name, and with the page. */
export function useDocumentTitle(page?: string): void {
  const systemName = useSystemName();

  useEffect(() => {
    document.title = page ? `${page} · ${systemName}` : systemName;
  }, [page, systemName]);
}
